import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import KeepAlive, { useActivate, useUnactivate } from 'react-activation';

import { logging, logPostInfo } from '../../actions/log';
import { getAuthorList } from '../../actions/wallpaper';
import ScrollTop from '../../components/ScrollTop/ScrollTop';
import { CONTEXT } from '../../config';
import wpAuthorDefault from '../../static/wp_author_default.png';
import Blank from './Blank';
import styles from './WallPaperAuthorList.module.less';

const Author = memo(({ info = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  const handleClick = useCallback(() => {
    // 点击某个作者
    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'C_Wallpaper_AuthorMark',
        label: '',
        param2: {
          source: `Wallpaper_AuthorList`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          authorId: info.authorId,
          authorName: info.authorName,
        },
      }),
    );
    history.push(`${CONTEXT}/wpauthordetail?authorId=${info.authorId}`);
  });

  const handelFormat = useCallback((num = 0) => {
    if (num >= 100000000) {
      return `${Math.round((num / 10000 / 10000) * 10) / 10}亿`;
    } else if (num >= 10000) {
      return `${Math.round((num / 10000) * 10) / 10}万`;
    } else {
      return num;
    }
  }, []);

  return (
    <div className={styles.author} onClick={handleClick}>
      <img src={info.headPortrait || wpAuthorDefault} alt="" className={styles.author_head} />
      <div className={styles.author_name}>{info.authorName}</div>
      <div className={styles.author_other}>
        <div className={styles.author_other_item}>
          {handelFormat(info.wallpaperCount || 0)}&nbsp;作品
        </div>
        <div className={styles.author_other_item}>|</div>
        <div className={styles.author_other_item}>
          {handelFormat(info.collectCount || 0)}&nbsp;收藏
        </div>
      </div>
    </div>
  );
});

const selectWpAuthorList = createSelector(
  state => {
    return state.wallpaper && state.wallpaper;
  },
  info => info.wpAuthorList,
);
const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

function WPAuthorList() {
  // export default memo(() => {
  const dispatch = useDispatch();
  const { action } = useHistory();
  const [pageContent, setPageContent] = useState(null);
  const logInfoData = useSelector(selectLogInfo);
  const authorList = useSelector(selectWpAuthorList);

  const [initTime, setInitTime] = useState(+new Date());

  // 浏览时长打桩
  const leaveTimeLog = useCallback(() => {
    const leaveTime = +new Date();
    dispatch(
      logging({
        category: 'I',
        action: 'View_Time',
        label: '',
        param2: {
          source: `Wallpaper_AuthorList`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          initTime,
          leaveTime,
          time: leaveTime - initTime,
        },
      }),
    );
  }, [initTime]);

  const pagePvLog = useCallback(() => {
    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'P_Wallpaper_AuthorList',
        label: '',
        param2: {
          source: 'Wallpaper_AuthorList',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        },
      }),
    );
    dispatch(
      logPostInfo({
        source: 'Wallpaper_AuthorList',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
      }),
    );
  });

  useActivate(() => {
    setInitTime(+new Date());
  });
  useUnactivate(() => {
    leaveTimeLog();
  });

  useEffect(() => {
    pagePvLog();
  }, []);
  useActivate(() => {
    pagePvLog();
  });

  /**
   * 统计页面浏览时长
   */
  useEffect(() => {
    const initTime = +new Date();
    return () => {
      const leaveTime = +new Date();
      dispatch(
        logging({
          category: 'I',
          action: 'View_Time',
          label: '',
          param2: {
            source: `Wallpaper_AuthorList`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            initTime,
            leaveTime,
            time: leaveTime - initTime,
          },
        }),
      );
    };
  }, []);

  useEffect(() => {
    // 更新页面 PV
    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'P_Wallpaper_AuthorList',
        label: '',
        param2: {
          source: `Wallpaper_AuthorList`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        },
      }),
    );
    dispatch(
      logPostInfo({
        source: `Wallpaper_AuthorList`,
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
      }),
    );
  }, [dispatch]);

  // 获取全部作者列表
  useEffect(() => {
    if (action !== 'POP' || authorList.list.length === 0) {
      dispatch(
        getAuthorList({
          ...authorList.pagenation,
          skip: 0,
          refresh: true,
        }),
      );
    }
  }, []);
  useActivate(() => {
    if (action !== 'POP' || authorList.list.length === 0) {
      dispatch(
        getAuthorList({
          ...authorList.pagenation,
          skip: 0,
          refresh: true,
        }),
      );
    }
  });

  const loadMore = useCallback(() => {
    if (!authorList.load) {
      dispatch(
        getAuthorList({
          ...authorList.pagenation,
          skip: authorList.list.length,
          refresh: false,
        }),
      );
    }
  });

  return (
    <div className={styles.page} ref={r => setPageContent(r)}>
      <div className={styles.page_container}>
        <div className={styles.header}>全部作者</div>
        {authorList.list.length < 1 ? (
          <Blank load={authorList.load} />
        ) : (
          <InfiniteScroll
            pageStart={1}
            loadMore={loadMore}
            hasMore={authorList.list.length < authorList.pagenation.count}
            loader={
              <div className="loader" key={0}>
                ----------加载中----------
              </div>
            }
            initialLoad={false}
            useWindow={false}
            getScrollParent={() => pageContent}
          >
            <div className={styles.content}>
              {authorList.list.map((item, index) => {
                return (
                  <div
                    className={classNames(styles.content_author)}
                    key={`${item.authorId}_${index}`}
                  >
                    <Author info={item} />
                  </div>
                );
              })}
              {authorList.list.length >= authorList.pagenation.count &&
                authorList.pagenation.count > 12 && <></>}
            </div>
          </InfiniteScroll>
        )}
        <ScrollTop node={pageContent} />
      </div>
    </div>
  );
}
// });

export default () => (
  <KeepAlive name="wpauthordetial">
    <WPAuthorList />
  </KeepAlive>
);
